import React, { forwardRef } from 'react';

import Badge from '@atlaskit/badge';
import { LinkButton, type LinkButtonProps } from '@atlaskit/button/new';
import Tooltip from '@atlaskit/tooltip';

import { UiViewedEvent } from '../analytics/ui-viewed-event';
import type { ButtonContent } from '../services/use-button-content';

import { getIconElement } from './get-icon-element';

interface EditionAwarenessButtonLinkAdsProps extends Pick<LinkButtonProps, 'href' | 'onClick'> {
	analyticsAttributes: Record<string, unknown>;
	buttonContent: ButtonContent;
	onMouseEnter?: () => void;
}

export const EditionAwarenessButtonLinkAds: React.ForwardRefExoticComponent<
	EditionAwarenessButtonLinkAdsProps & React.RefAttributes<HTMLAnchorElement>
> = forwardRef<HTMLAnchorElement, EditionAwarenessButtonLinkAdsProps>(
	function EditionAwarenessButtonLinkAds(
		{ buttonContent, href, onClick, analyticsAttributes, onMouseEnter },
		_ref,
	) {
		const { text, appearance = 'default', tooltip, icon, badge } = buttonContent;

		const iconElement = getIconElement(icon);
		const iconBefore = iconElement ? () => iconElement : undefined;
		const iconAfter = badge ? () => <Badge appearance="discovery">{badge}</Badge> : undefined;

		return (
			<Tooltip content={tooltip}>
				{(tooltipProps) => {
					const { ref: tooltipRef, ...restTooltipProps } = tooltipProps;

					return (
						<LinkButton
							{...restTooltipProps}
							ref={tooltipRef as React.Ref<HTMLAnchorElement>}
							href={href}
							appearance={appearance as LinkButtonProps['appearance']}
							onClick={onClick}
							onMouseEnter={onMouseEnter}
							iconBefore={iconBefore}
							iconAfter={iconAfter}
						>
							{text}
							<UiViewedEvent
								actionSubjectId="editionAwareness"
								actionSubject="button"
								attributes={analyticsAttributes}
							/>
						</LinkButton>
					);
				}}
			</Tooltip>
		);
	},
);
